import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

/**
 * Reusable generic confirmation modal
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {string} title - Modal title (defaults to "Confirm Action")
 * @param {string} mainTitle - Legacy prop name for title (for backward compatibility)
 * @param {string} message - Confirmation message
 * @param {string} mainMessage - Legacy prop name for message (for backward compatibility)
 * @param {string} confirmLabel - Custom confirm button text (defaults to "Confirm")
 * @param {string} textConfirm - Legacy prop name for confirmLabel (for backward compatibility)
 * @param {string} cancelLabel - Custom cancel button text (defaults to "Cancel")
 * @param {string} textCancel - Legacy prop name for cancelLabel (for backward compatibility)
 * @param {Function} onConfirm - Callback when confirm button is clicked
 * @param {Function} onCancel - Callback when cancel button is clicked or modal is closed
 * @param {boolean} loading - Shows loading state on confirm button
 * @param {string} severity - Visual style: 'info', 'warning', 'error' (defaults to 'info')
 * @param {boolean} showIcon - Whether to show the icon (defaults to true)
 */
function ConfirmModal({
  isOpen,
  title,
  mainTitle, // Legacy prop for backward compatibility
  message,
  mainMessage, // Legacy prop for backward compatibility
  confirmLabel,
  textConfirm, // Legacy prop for backward compatibility
  cancelLabel,
  textCancel, // Legacy prop for backward compatibility
  onConfirm,
  onCancel,
  loading = false,
  severity = 'info',
  showIcon = true
}) {
  // Support both old and new prop names for backward compatibility
  const resolvedTitle = title || mainTitle || 'Confirm Action';
  const resolvedMessage = message || mainMessage;
  const resolvedConfirmLabel = confirmLabel || textConfirm || 'Confirm';
  const resolvedCancelLabel = cancelLabel || textCancel || 'Cancel';

  // Color mapping based on severity
  const severityConfig = {
    info: {
      borderColor: 'primary.main',
      iconBg: 'primary.lighter',
      iconColor: 'primary.main',
      buttonColor: 'primary'
    },
    warning: {
      borderColor: 'warning.main',
      iconBg: 'warning.lighter',
      iconColor: 'warning.main',
      buttonColor: 'warning'
    },
    error: {
      borderColor: 'error.main',
      iconBg: 'error.lighter',
      iconColor: 'error.main',
      buttonColor: 'error'
    }
  };

  const config = severityConfig[severity] || severityConfig.info;

  return (
    <Dialog
      open={isOpen}
      onClose={loading ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      {/* Subtle colored top border for visual indication */}
      <Box
        sx={{
          height: 4,
          backgroundColor: config.borderColor
        }}
      />

      <DialogTitle
        sx={{
          pt: 3,
          pb: 2,
          px: 3,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {resolvedTitle}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start'
          }}
        >
          {showIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: config.iconBg,
                color: config.iconColor,
                flexShrink: 0
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 28 }} />
            </Box>
          )}

          <Box sx={{ flex: 1, pt: showIcon ? 1.5 : 0 }}>
            {resolvedMessage && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary' }}
              >
                {resolvedMessage}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          variant="outlined"
          sx={{ textTransform: 'none' }}
        >
          {resolvedCancelLabel}
        </Button>

        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color={config.buttonColor}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ textTransform: 'none', minWidth: 100 }}
        >
          {loading ? 'Processing...' : resolvedConfirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmModal;